import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { getBusinessUnits } from 'lib/api/business';
import { UpdateForm } from 'lib/api/sgr/types';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import { isAdmin, rutChanger, validaRut } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import BusinessUnitsDialog from '../Common/BusinessUnitsDialog';

type Props = {
  form: UpdateForm;
  setForm: (form: UpdateForm) => void;
};

const UpdateWarrantyForm: React.FC<Props> = ({ form, setForm }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const userIsAdmin = auth.user ? isAdmin(auth.user) : false;
  const [open, setOpen] = useState(false);
  const [unitName, setUnitName] = useState<string | null>(null);
  const { data: unitsData, isPending: isLoadingUnit } = getBusinessUnits(dispatch, form.principalRut);

  const updateField = (updates: Partial<UpdateForm>) => {
    setForm({ ...form, ...updates });
  };

  useEffect(() => {
    if (!form.unitCode) { setUnitName(null); return; }
    const unit = unitsData?.units.find((u) => u.unitCode === form.unitCode);
    if (unit) setUnitName(unit.name);
  }, [unitsData, form.unitCode]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="ID Licitación"
            value={form?.tenderId}
            onChange={(e) => updateField({ tenderId: e.target.value })}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Tipo de Garantía"
            value={form.type}
            onChange={(e) => updateField({ type: e.target.value })}
            required
          >
            {SGR_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Monto Cotizado"
            type="number"
            value={form.quotedAmount}
            onChange={(e) =>
              updateField({ quotedAmount: Number(e.target.value) })
            }
            inputProps={{ step: 'any' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Select
                    value={form.quotedCurrency}
                    onChange={(e) =>
                      updateField({ quotedCurrency: e.target.value })
                    }
                    variant="standard"
                  >
                    {CURRENCIES.map((currency) => (
                      <MenuItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              ),
            }}
            required
          />
        </Grid>

        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="fogape-select-label">Fogape</InputLabel>
            <Select
              labelId="fogape-select-label"
              label="Fogape"
              value={form.fogape}
              onChange={(e) => updateField({ fogape: Number(e.target.value) })}
            >
              {Array.from({ length: 11 }, (_, i) => (
                <MenuItem key={i * 10} value={i * 10}>
                  {i * 10}%
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha Inicio"
              value={form.startDate}
              onChange={(date) => updateField({ startDate: date })}
              minDate={userIsAdmin ? undefined : dayjs().subtract(7, 'day')}
              maxDate={userIsAdmin ? undefined : dayjs().add(7, 'day')}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { fullWidth: true, required: true },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha Término"
              value={form.endDate}
              onChange={(date) => updateField({ endDate: date })}
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="RUT Mandante"
            value={form.principalRut}
            onChange={(e) =>
              rutChanger(e.target.value, (val) =>
                updateField({ principalRut: val }),
              )
            }
            error={!!form.principalRut && !validaRut(form.principalRut)}
            helperText={
              !!form.principalRut && !validaRut(form.principalRut)
                ? 'RUT Mandante inválido'
                : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setOpen(true)}
                    disabled={
                      !form.principalRut || !validaRut(form.principalRut)
                    }
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Unidad de Negocio"
            value={form.unitCode ?? ''}
            onChange={(e) => {
              setUnitName(null);
              updateField({ unitCode: e.target.value });
            }}
            InputLabelProps={{ shrink: Boolean(form.unitCode) }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            InputProps={
              isLoadingUnit && form.unitCode
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ),
                  }
                : unitName
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={unitName}>
                            <InfoOutlinedIcon fontSize="small" />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }
                  : undefined
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Dirección Mandante"
            value={form.address ?? ''}
            onChange={(e) => updateField({ address: e.target.value })}
            InputLabelProps={{ shrink: Boolean(form.address) }}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label="Glosa"
            value={form.description}
            onChange={(e) => updateField({ description: e.target.value })}
            multiline
            minRows={3}
            maxRows={3}
          />
        </Grid>
      </Grid>
      {open && (
        <BusinessUnitsDialog
          open={open}
          onClose={() => setOpen(false)}
          form={form}
          setForm={setForm}
          canEditAddress
        />
      )}
    </>
  );
};

export default UpdateWarrantyForm;
